import { useAuth } from "@workos-inc/authkit-react";
import {
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  ChartNoAxesCombined,
  CircleHelp,
  LogOut,
  Megaphone,
  PanelRightClose,
  PanelRightOpen,
  Settings,
  SquareUser,
  Timer,
  User,
} from "lucide-react";
import { createSerializer } from "nuqs";
import type React from "react";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { EntryFilterState } from "@/features/attorney/views/Entries/filters/types";
import { filterStateParser } from "@/features/attorney/views/Entries/filters/urlState";
import { useSettingsCache } from "@/shared/cache/slices/settings";
import { BrandLogo } from "@/shared/components/BrandLogo";
import { ProfileMenu, type ProfileMenuItem } from "@/shared/components/ProfileMenu/ProfileMenu";
import { Button } from "@/shared/components/ui/button";
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/shared/components/ui/sidebar";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { hasAnyAdminAccess } from "@/shared/config/adminPermissions";
import { isHourglassInternal } from "@/shared/config/hourglassPermissions";
import { getViewFromPath, type ViewMode, viewToRoute } from "@/shared/config/routes";
import { useUpdateNotification } from "@/shared/hooks/useUpdateNotification";

type View = ViewMode;

const entryStatusSubtabs = [
  { id: "tracked", label: "Tracked", values: ["tracked", "reviewed"] },
  { id: "exported", label: "Exported", values: ["exported"] },
  { id: "deleted", label: "Deleted", values: ["deleted"] },
] as const;

type EntryStatusSubtab = (typeof entryStatusSubtabs)[number];
type EntryStatusSubtabId = EntryStatusSubtab["id"];

const serializeEntryFilters = createSerializer({ filter: filterStateParser });

function hasSameValues(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

function getCurrentFilters(search: string): EntryFilterState {
  const params = new URLSearchParams(search);
  return filterStateParser.parse(params.get("filter") ?? "") ?? { status: [] };
}

function applyEntryStatusFilter(
  search: string,
  subtab: EntryStatusSubtab,
): EntryFilterState {
  return {
    ...getCurrentFilters(search),
    status: [...subtab.values],
  };
}

function buildEntryStatusUrl(
  search: string,
  subtab: EntryStatusSubtab,
): string {
  return serializeEntryFilters(`${viewToRoute.entries}${search}`, {
    filter: applyEntryStatusFilter(search, subtab),
  });
}

function getActiveEntryStatus(search: string): EntryStatusSubtabId | null {
  const statusValues = getCurrentFilters(search).status ?? [];
  return (
    entryStatusSubtabs.find((subtab) =>
      hasSameValues(statusValues, subtab.values),
    )?.id ?? null
  );
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const showAdminLink = hasAnyAdminAccess(user?.email);
  const showInternalLink = isHourglassInternal(user?.email);
  const { settings } = useSettingsCache();
  const { hasNewUpdate, dismissUpdate } = useUpdateNotification();
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";

  const viewMode: View = useMemo(() => {
    return getViewFromPath(location.pathname) || "entries";
  }, [location.pathname]);

  const currentSearch =
    typeof window !== "undefined" ? window.location.search : location.search;
  const activeEntryStatus = getActiveEntryStatus(currentSearch);

  const userInitials = useMemo(() => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "?";
  }, [user]);

  const userDisplayName = useMemo(() => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email ?? "";
  }, [user]);

  // Read window.location.search (not react-router's location.search) so filters
  // carry across tabs: nuqs writes filter/date params straight to the History
  // API, and useLocation() can lag behind those writes — reading the live URL
  // keeps the current filters/date in the query string we navigate with.
  const navigateTo = (view: View) => {
    if (view === "entries") {
      navigate(buildEntryStatusUrl(window.location.search, entryStatusSubtabs[0]));
      return;
    }

    navigate(viewToRoute[view] + window.location.search);
  };

  const navItems: Array<{
    view: View;
    label: string;
    icon: React.ElementType;
  }> = [
    { view: "entries", label: "Entries", icon: Timer },
    { view: "calendar", label: "Calendar", icon: Calendar },
    {
      view: "reporting" as View,
      label: "Reporting",
      icon: ChartNoAxesCombined,
    },
  ];

  const profileMenuItems: ProfileMenuItem[] = [
    {
      label: "Settings",
      icon: Settings,
      isActive: viewMode === "settings",
      onClick: () => navigateTo("settings"),
    },
    {
      label: "Support",
      icon: User,
      isActive: viewMode === "support",
      onClick: () => navigateTo("support"),
    },
    {
      label: "FAQ",
      icon: CircleHelp,
      isActive: viewMode === "faq",
      onClick: () => navigateTo("faq"),
    },
    {
      label: "Docs",
      icon: BookOpen,
      isActive: viewMode === "docs",
      onClick: () => navigateTo("docs"),
      separator: true,
    } as ProfileMenuItem,
    {
      label: "Updates",
      icon: Megaphone,
      isActive: viewMode === "updates",
      onClick: () => {
        dismissUpdate();
        navigateTo("updates");
      },
      badge: hasNewUpdate,
    },
    {
      label: "Sign out",
      icon: LogOut,
      onClick: () => signOut({ returnTo: window.location.origin }),
      separator: true,
    },
  ];

  return (
    <ShadcnSidebar
      collapsible="icon"
      className="bg-sidebar border-r border-sidebar-border [&_[data-sidebar=sidebar]]:bg-sidebar"
    >
      <SidebarHeader className="px-4 pt-7 pb-2 text-sidebar-foreground group-data-[collapsible=icon]:px-0">
        <div className="flex h-7 items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <BrandLogo
            className="h-7 w-auto group-data-[collapsible=icon]:hidden"
            aria-label="Hourglass"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="mb-1 size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground [&_svg]:size-5"
          >
            {collapsed ? <PanelRightClose /> : <PanelRightOpen />}
          </Button>
        </div>
        {showAdminLink && collapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                aria-label="Switch to Admin view"
                className="mx-auto mt-3 hidden size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:flex [&_svg]:size-5"
              >
                <Building2 />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Switch to Admin</TooltipContent>
          </Tooltip>
        )}
        {showInternalLink && collapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/internal")}
                aria-label="Switch to Internal view"
                className="mx-auto mt-2 hidden size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:flex [&_svg]:size-5"
              >
                <SquareUser />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Switch to Internal</TooltipContent>
          </Tooltip>
        )}
        {(showAdminLink || showInternalLink) && (
          <div className="mt-2 -mx-6 px-4 group-data-[collapsible=icon]:hidden">
            <Tabs
              defaultValue="timekeeper"
              onValueChange={(value) => {
                if (value === "admin") navigate("/admin");
                else if (value === "internal") navigate("/internal");
              }}
            >
              <TabsList className="flex bg-transparent rounded-none border-b border-sidebar-border w-full gap-0 p-0 h-auto">
                <TabsTrigger
                  value="timekeeper"
                  className="flex-1 rounded-none border-b-2 border-transparent bg-transparent px-2 py-1.5 text-xs text-sidebar-foreground/60 shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:text-sidebar-foreground data-[state=active]:shadow-none gap-1.5"
                >
                  <Briefcase className="size-3.5" />
                  Timekeeper
                </TabsTrigger>
                {showAdminLink && (
                  <TabsTrigger
                    value="admin"
                    className="flex-1 rounded-none border-b-2 border-transparent bg-transparent px-2 py-1.5 text-xs text-sidebar-foreground/60 shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:text-sidebar-foreground data-[state=active]:shadow-none gap-1.5"
                  >
                    <Building2 className="size-3.5" />
                    Admin
                  </TabsTrigger>
                )}
                {showInternalLink && (
                  <TabsTrigger
                    value="internal"
                    className="flex-1 rounded-none border-b-2 border-transparent bg-transparent px-2 py-1.5 text-xs text-sidebar-foreground/60 shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:text-sidebar-foreground data-[state=active]:shadow-none gap-1.5"
                  >
                    <SquareUser className="size-3.5" />
                    Internal
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const showEntrySubtabs =
                  item.view === "entries" && viewMode === "entries";

                return (
                  <SidebarMenuItem key={item.view}>
                    <SidebarMenuButton
                      isActive={viewMode === item.view}
                      tooltip={item.label}
                      onClick={() => navigateTo(item.view)}
                      className="h-10 px-3 gap-3 data-[active=true]:font-normal [&>svg]:size-5"
                    >
                      <item.icon />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                    {item.view === "entries" && (
                      <div
                        aria-hidden={!showEntrySubtabs}
                        className={`grid transition-[grid-template-rows,opacity,transform] duration-200 ease-out group-data-[collapsible=icon]:hidden ${
                          showEntrySubtabs
                            ? "grid-rows-[1fr] translate-y-0 opacity-100"
                            : "grid-rows-[0fr] -translate-y-1 opacity-0 pointer-events-none"
                        }`}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <SidebarMenuSub className="mt-1 mb-1.5 gap-0.5 border-sidebar-border/70 py-0.5">
                            {entryStatusSubtabs.map((subtab) => {
                              const href = buildEntryStatusUrl(
                                currentSearch,
                                subtab,
                              );

                              return (
                                <SidebarMenuSubItem key={subtab.id}>
                                  <SidebarMenuSubButton
                                    href={href}
                                    size="sm"
                                    isActive={activeEntryStatus === subtab.id}
                                    tabIndex={showEntrySubtabs ? undefined : -1}
                                    onClick={(event) => {
                                      if (
                                        event.defaultPrevented ||
                                        event.button !== 0 ||
                                        event.metaKey ||
                                        event.ctrlKey ||
                                        event.shiftKey ||
                                        event.altKey
                                      ) {
                                        return;
                                      }

                                      event.preventDefault();
                                      navigate(
                                        buildEntryStatusUrl(
                                          window.location.search,
                                          subtab,
                                        ),
                                      );
                                    }}
                                    className="h-6 px-2 text-xs font-normal text-sidebar-foreground/55 hover:text-sidebar-foreground/80 data-[active=true]:bg-sidebar-accent/60 data-[active=true]:text-sidebar-foreground/75"
                                  >
                                    <span>{subtab.label}</span>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </div>
                      </div>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <ProfileMenu
          userDisplayName={userDisplayName}
          userInitials={userInitials}
          userEmail={user?.firstName ? user.email : undefined}
          subtitle={settings?.prof_type || undefined}
          menuItems={profileMenuItems}
        />
      </SidebarFooter>
    </ShadcnSidebar>
  );
};

export default Sidebar;
